import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  createOnlineItem,
  listItems,
  listOnlineItems,
  getCategories,
  getGroupTypes,
} from '../../../endpoints/chapterSupplies';
import { getDocumentList } from '../../../endpoints/documents';
import type {
  CreateOnlineItemRequest,
  KitComponentInput,
  VariantInput,
} from '../../../types/chapterSupplies';
import type { DocumentList } from '../../../types/document';

function selectedNames(
  ids: string[],
  options: { id: string; name: string }[],
  maxShow = 3
): string {
  if (!ids.length) return 'None';
  const names = ids
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean) as string[];
  if (names.length > maxShow) {
    return `${names.slice(0, maxShow).join(', ')} +${names.length - maxShow} more`;
  }
  return names.join(', ');
}

const ITEM_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'kit', label: 'Kit' },
  { value: 'variant', label: 'Variant' },
  { value: 'personalized', label: 'Personalized' },
  { value: 'custom_text', label: 'Custom text' },
];

const initialForm: CreateOnlineItemRequest = {
  item_id: null,
  item_type: 'individual',
  price: 0,
  is_featured: 0,
  is_active: 1,
  name: null,
  short_description: null,
  long_description: null,
  tags: null,
  sort_order: 0,
  library_document_id: null,
  max_quantity_per_order: null,
  max_text_length: null,
  max_text_lines: 1,
  category_ids: [],
  sub_category_ids: [],
  group_type_ids: [],
  kit_components: [],
  variants: [],
  order_approval_required: 0,
};

const DOCUMENT_SEARCH_DEBOUNCE_MS = 300;

export default function OnlineItemCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateOnlineItemRequest>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [documentSearchInput, setDocumentSearchInput] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(
      () => setDocumentSearch(documentSearchInput),
      DOCUMENT_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [documentSearchInput]);

  const { data: itemsData } = useApiGet(
    ['chapter-supplies-items', 500, 0],
    () => listItems({ limit: 500, offset: 0 }),
    { enabled: true, retry: 1 }
  );
  const { data: onlineItemsData } = useApiGet(
    ['chapter-supplies-online-items', 500, 0],
    () => listOnlineItems({ limit: 500, offset: 0 }),
    { enabled: true, retry: 1 }
  );
  const { data: categories = [] } = useApiGet(
    ['chapter-supplies-categories'],
    getCategories,
    { enabled: true, retry: 1 }
  );
  const { data: groupTypes = [] } = useApiGet(
    ['chapter-supplies-group-types'],
    getGroupTypes,
    { enabled: true, retry: 1 }
  );
  const { data: documentsData } = useApiGet(
    ['library-documents-search', documentSearch],
    () =>
      getDocumentList({
        search: documentSearch || undefined,
        per_page: 50,
        order: 'asc',
        sort_by: 'name',
      }),
    { enabled: true, retry: 1 }
  );
  const libraryDocuments: DocumentList[] = documentsData?.data ?? [];

  const allSubcategories = useMemo(
    () => categories.flatMap((c) => c.subcategories ?? []),
    [categories]
  );

  const availableItems = useMemo(() => {
    const items = itemsData?.items ?? [];
    const onlineItemIds = new Set(
      (onlineItemsData?.online_items ?? [])
        .map((o) => o.item_id)
        .filter((id): id is string => id != null && id !== '')
    );
    return items.filter((i) => !onlineItemIds.has(i.id));
  }, [itemsData?.items, onlineItemsData?.online_items]);

  const requiresItem = form.item_type !== 'kit' && form.item_type !== 'variant';
  const requiresPriceCategoriesShortDesc = [
    'individual',
    'personalized',
    'custom_text',
  ].includes(form.item_type ?? '');
  const requiresMaxTextLength = ['personalized', 'custom_text'].includes(
    form.item_type ?? ''
  );

  const { mutate: submitOnlineItem, isPending } = useApiSend(
    (data: CreateOnlineItemRequest) => createOnlineItem(data),
    (response) => {
      navigate(`/chapter-supplies-management/online-items/${response.id}`);
    },
    (err) => {
      setError(err?.message ?? 'Failed to create online item');
    },
    [['chapter-supplies-online-items']]
  );

  const handleChange = (
    field: keyof CreateOnlineItemRequest,
    value:
      | string
      | number
      | null
      | undefined
      | string[]
      | KitComponentInput[]
      | VariantInput[]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value ?? null }));
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.item_type?.trim()) {
      setError('Item type is required');
      return;
    }
    if (requiresItem && !form.item_id?.trim()) {
      setError('Item is required when item type is not Kit');
      return;
    }
    if (requiresPriceCategoriesShortDesc) {
      if (!form.short_description?.trim()) {
        setError('Short description is required for this item type');
        return;
      }
      if (!(form.category_ids?.length ?? 0)) {
        setError('At least one category is required for this item type');
        return;
      }
    }
    if (requiresMaxTextLength) {
      if (form.max_text_length == null || Number(form.max_text_length) <= 0) {
        setError(
          'Max text length is required for personalized and custom text items'
        );
        return;
      }
      if (form.max_text_lines == null || Number(form.max_text_lines) <= 0) {
        setError(
          'Max text lines is required for personalized and custom text items'
        );
        return;
      }
    }
    if (form.item_type === 'kit') {
      if (!form.name?.trim()) {
        setError('Kit name is required');
        return;
      }
      const validKitComponents = (form.kit_components ?? []).filter(
        (k) => k.item_id?.trim() && k.quantity > 0
      );
      if (!validKitComponents.length) {
        setError(
          'Kit must have at least one component with an item and quantity'
        );
        return;
      }
    }
    if (form.item_type === 'variant') {
      if (!form.name?.trim()) {
        setError('Name is required for variant items');
        return;
      }
      const validVariants = (form.variants ?? []).filter((v) => v.price >= 0);
      if (!validVariants.length) {
        setError(
          'Variant must have at least one option with price 0 or greater'
        );
        return;
      }
    }
    if (form.item_type !== 'variant' && form.price < 0) {
      setError('Price must be 0 or greater');
      return;
    }
    const payload: CreateOnlineItemRequest = {
      item_type: form.item_type.trim().toLowerCase(),
      item_id:
        requiresItem && form.item_id?.trim() ? form.item_id.trim() : null,
      price: form.item_type === 'variant' ? 0 : Number(form.price),
      is_featured: form.is_featured ?? 0,
      is_active: form.is_active ?? 1,
      short_description: form.short_description?.trim() || null,
      long_description: form.long_description?.trim() || null,
      tags: form.tags?.trim() || null,
      sort_order: form.sort_order ?? 0,
      library_document_id:
        form.item_type === 'kit' || form.item_type === 'variant'
          ? null
          : form.library_document_id?.trim() || null,
      max_quantity_per_order:
        form.max_quantity_per_order != null
          ? Number(form.max_quantity_per_order)
          : null,
      max_text_length:
        form.item_type === 'individual' ||
        form.item_type === 'kit' ||
        form.item_type === 'variant'
          ? null
          : form.max_text_length != null
            ? Number(form.max_text_length)
            : null,
      max_text_lines:
        form.item_type === 'individual' ||
        form.item_type === 'kit' ||
        form.item_type === 'variant'
          ? null
          : form.max_text_lines != null
            ? Number(form.max_text_lines)
            : null,
      category_ids: form.category_ids ?? [],
      sub_category_ids: form.sub_category_ids ?? [],
      group_type_ids: form.group_type_ids ?? [],
      kit_components:
        form.item_type === 'kit'
          ? (form.kit_components ?? []).filter(
              (k) => k.item_id?.trim() && k.quantity > 0
            )
          : undefined,
      name: form.name?.trim() || null,
      variants:
        form.item_type === 'variant'
          ? (form.variants ?? []).filter((v) => v.price >= 0)
          : undefined,
      order_approval_required: form.order_approval_required ?? 0,
    };
    submitOnlineItem(payload);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Create online item"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() =>
                navigate('/chapter-supplies-management/online-items')
              }
            >
              Back to list
            </Button>
          }
          rightDom={undefined}
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="item-type-label">Item type</InputLabel>
                <Select
                  labelId="item-type-label"
                  label="Item type"
                  value={form.item_type ?? 'individual'}
                  onChange={(e) => {
                    const v = e.target.value as string;
                    handleChange('item_type', v);
                    if (v === 'kit') {
                      handleChange('item_id', null);
                      handleChange('library_document_id', null);
                      handleChange('max_text_length', null);
                      handleChange('max_text_lines', null);
                      if (!(form.kit_components?.length ?? 0))
                        handleChange('kit_components', [
                          { item_id: '', quantity: 1 },
                        ]);
                    }
                    if (v === 'variant') {
                      handleChange('item_id', null);
                      handleChange('library_document_id', null);
                      handleChange('max_text_length', null);
                      handleChange('max_text_lines', null);
                      if (!(form.variants?.length ?? 0))
                        handleChange('variants', [
                          { item_id: null, price: 0, description: null },
                        ]);
                    }
                  }}
                >
                  {ITEM_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {requiresItem && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="item-id-label">Item</InputLabel>
                  <Select
                    labelId="item-id-label"
                    label="Item"
                    value={form.item_id ?? ''}
                    onChange={(e) =>
                      handleChange('item_id', e.target.value || null)
                    }
                  >
                    <MenuItem value="">
                      <em>Select an item (not yet online)</em>
                    </MenuItem>
                    {availableItems.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                        {item.sku ? ` (${item.sku})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {availableItems.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    All items already have an online listing, or no items exist
                    yet.
                  </Typography>
                )}
              </Grid>
            )}
            {form.item_type !== 'variant' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required={requiresPriceCategoriesShortDesc}
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label="Price"
                  value={form.price === 0 ? '' : form.price}
                  onChange={(e) =>
                    handleChange(
                      'price',
                      e.target.value === '' ? 0 : Number(e.target.value)
                    )
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_featured === 1}
                    onChange={(e) =>
                      handleChange('is_featured', e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Featured"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_active === 1}
                    onChange={(e) =>
                      handleChange('is_active', e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Active (visible in store)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!(form.order_approval_required ?? 0)}
                    onChange={(e) =>
                      handleChange(
                        'order_approval_required',
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Order approval required"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={
                  requiresPriceCategoriesShortDesc ||
                  form.item_type === 'kit' ||
                  form.item_type === 'variant'
                }
                multiline
                rows={2}
                label={
                  form.item_type === 'kit'
                    ? 'Kit name'
                    : form.item_type === 'variant'
                      ? 'Name'
                      : 'Short description'
                }
                value={
                  form.item_type === 'kit' || form.item_type === 'variant'
                    ? (form.name ?? '')
                    : (form.short_description ?? '')
                }
                onChange={(e) =>
                  form.item_type === 'kit' || form.item_type === 'variant'
                    ? handleChange('name', e.target.value)
                    : handleChange('short_description', e.target.value)
                }
              />
            </Grid>
            {form.item_type === 'kit' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Short description"
                  value={form.short_description ?? ''}
                  onChange={(e) =>
                    handleChange('short_description', e.target.value)
                  }
                />
              </Grid>
            )}
            {form.item_type === 'variant' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Short description (optional)"
                  value={form.short_description ?? ''}
                  onChange={(e) =>
                    handleChange('short_description', e.target.value)
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Long description"
                value={form.long_description ?? ''}
                onChange={(e) =>
                  handleChange('long_description', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tags"
                placeholder="Comma-separated for search"
                value={form.tags ?? ''}
                onChange={(e) => handleChange('tags', e.target.value)}
              />
            </Grid>
            {form.item_type !== 'individual' &&
              form.item_type !== 'kit' &&
              form.item_type !== 'variant' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required={requiresMaxTextLength}
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    label="Max text length"
                    value={
                      form.max_text_length != null
                        ? String(form.max_text_length)
                        : ''
                    }
                    onChange={(e) =>
                      handleChange(
                        'max_text_length',
                        e.target.value === '' ? null : Number(e.target.value)
                      )
                    }
                  />
                </Grid>
              )}
            {form.item_type !== 'individual' &&
              form.item_type !== 'kit' &&
              form.item_type !== 'variant' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required={requiresMaxTextLength}
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    label="Max text lines"
                    value={
                      form.max_text_lines != null
                        ? String(form.max_text_lines)
                        : ''
                    }
                    onChange={(e) =>
                      handleChange(
                        'max_text_lines',
                        e.target.value === '' ? null : Number(e.target.value)
                      )
                    }
                  />
                </Grid>
              )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Sort order"
                value={form.sort_order ?? ''}
                onChange={(e) =>
                  handleChange(
                    'sort_order',
                    e.target.value === '' ? 0 : Number(e.target.value)
                  )
                }
              />
            </Grid>
            {form.item_type !== 'kit' && form.item_type !== 'variant' && (
              <Grid item xs={12} sm={6}>
                <Autocomplete<DocumentList>
                  fullWidth
                  options={libraryDocuments}
                  getOptionLabel={(opt) => opt.name}
                  value={
                    form.library_document_id
                      ? (libraryDocuments.find(
                          (d) => d.id === form.library_document_id
                        ) ??
                        ({
                          id: form.library_document_id,
                          name: form.library_document_id,
                        } as DocumentList))
                      : null
                  }
                  onChange={(_, newValue) =>
                    handleChange('library_document_id', newValue?.id ?? null)
                  }
                  onInputChange={(_, value) => setDocumentSearchInput(value)}
                  filterOptions={(x) => x}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Library document"
                      placeholder="Search documents..."
                    />
                  )}
                />
              </Grid>
            )}
            {form.item_type === 'kit' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Kit components
                </Typography>
                {(form.kit_components ?? []).map((comp, idx) => (
                  <Grid
                    container
                    key={idx}
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`kit-item-${idx}`}>Item</InputLabel>
                        <Select
                          labelId={`kit-item-${idx}`}
                          label="Item"
                          value={comp.item_id ?? ''}
                          onChange={(e) => {
                            const next = [...(form.kit_components ?? [])];
                            next[idx] = {
                              ...next[idx],
                              item_id: e.target.value,
                              quantity: next[idx]?.quantity ?? 1,
                            };
                            handleChange('kit_components', next);
                          }}
                        >
                          <MenuItem value="">
                            <em>Select item</em>
                          </MenuItem>
                          {(itemsData?.items ?? []).map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                              {item.sku ? ` (${item.sku})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                        label="Quantity"
                        value={comp.quantity ?? 1}
                        onChange={(e) => {
                          const next = [...(form.kit_components ?? [])];
                          next[idx] = {
                            ...next[idx],
                            item_id: next[idx]?.item_id ?? '',
                            quantity:
                              e.target.value === ''
                                ? 1
                                : Number(e.target.value),
                          };
                          handleChange('kit_components', next);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const next = (form.kit_components ?? []).filter(
                            (_, i) => i !== idx
                          );
                          handleChange('kit_components', next);
                        }}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    handleChange('kit_components', [
                      ...(form.kit_components ?? []),
                      { item_id: '', quantity: 1 },
                    ])
                  }
                >
                  Add component
                </Button>
              </Grid>
            )}
            {form.item_type === 'variant' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Variants
                </Typography>
                {(form.variants ?? []).map((v, idx) => (
                  <Grid
                    container
                    key={idx}
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`variant-item-${idx}`}>
                          Item (optional)
                        </InputLabel>
                        <Select
                          labelId={`variant-item-${idx}`}
                          label="Item (optional)"
                          value={v.item_id ?? ''}
                          onChange={(e) => {
                            const next = [...(form.variants ?? [])];
                            next[idx] = {
                              ...next[idx],
                              item_id: e.target.value || null,
                              price: next[idx]?.price ?? 0,
                              description: next[idx]?.description ?? null,
                            };
                            handleChange('variants', next);
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {(itemsData?.items ?? []).map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                              {item.sku ? ` (${item.sku})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        fullWidth
                        required
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        label="Price"
                        value={v.price ?? 0}
                        onChange={(e) => {
                          const next = [...(form.variants ?? [])];
                          next[idx] = {
                            ...next[idx],
                            item_id: next[idx]?.item_id ?? null,
                            price:
                              e.target.value === ''
                                ? 0
                                : Number(e.target.value),
                            description: next[idx]?.description ?? null,
                          };
                          handleChange('variants', next);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        label="Description (optional)"
                        value={v.description ?? ''}
                        onChange={(e) => {
                          const next = [...(form.variants ?? [])];
                          next[idx] = {
                            ...next[idx],
                            item_id: next[idx]?.item_id ?? null,
                            price: next[idx]?.price ?? 0,
                            description: e.target.value?.trim() || null,
                          };
                          handleChange('variants', next);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const next = (form.variants ?? []).filter(
                            (_, i) => i !== idx
                          );
                          handleChange('variants', next);
                        }}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    handleChange('variants', [
                      ...(form.variants ?? []),
                      { item_id: null, price: 0, description: null },
                    ])
                  }
                >
                  Add variant
                </Button>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Max quantity per order"
                value={
                  form.max_quantity_per_order != null
                    ? String(form.max_quantity_per_order)
                    : ''
                }
                onChange={(e) =>
                  handleChange(
                    'max_quantity_per_order',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                required={requiresPriceCategoriesShortDesc}
              >
                <InputLabel id="categories-label">Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  label="Categories"
                  multiple
                  value={form.category_ids ?? []}
                  onChange={(e) =>
                    handleChange('category_ids', e.target.value as string[])
                  }
                  renderValue={(selected) =>
                    selectedNames(selected as string[], categories)
                  }
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="subcategories-label">Subcategories</InputLabel>
                <Select
                  labelId="subcategories-label"
                  label="Subcategories"
                  multiple
                  value={form.sub_category_ids ?? []}
                  onChange={(e) =>
                    handleChange('sub_category_ids', e.target.value as string[])
                  }
                  renderValue={(selected) =>
                    selectedNames(selected as string[], allSubcategories)
                  }
                >
                  {allSubcategories.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="group-types-label">Group types</InputLabel>
                <Select
                  labelId="group-types-label"
                  label="Group types"
                  multiple
                  value={form.group_type_ids ?? []}
                  onChange={(e) =>
                    handleChange('group_type_ids', e.target.value as string[])
                  }
                  renderValue={(selected) =>
                    selectedNames(selected as string[], groupTypes)
                  }
                >
                  {groupTypes.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  isPending ||
                  !form.item_type?.trim() ||
                  (requiresItem && !form.item_id?.trim()) ||
                  (form.item_type !== 'variant' && form.price < 0) ||
                  (form.item_type === 'kit' &&
                    !(form.kit_components ?? []).filter(
                      (k) => k.item_id?.trim() && k.quantity > 0
                    ).length) ||
                  (form.item_type === 'kit' && !form.name?.trim()) ||
                  (form.item_type === 'variant' && !form.name?.trim()) ||
                  (form.item_type === 'variant' &&
                    !(form.variants ?? []).filter((v) => v.price >= 0).length)
                }
              >
                {isPending ? 'Creating…' : 'Create online item'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}
